"use client";

import { useCallback, useEffect, useRef, useId } from "react";
import { cn } from "@/lib/utils";
import ButtonV2 from "./ButtonV2";

export interface ConfirmDialogV2Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary";
  /** Disables the confirm button (e.g. while loading) */
  confirmDisabled?: boolean;
  /** Optional error text displayed inside the dialog */
  errorText?: string | null;
  className?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ConfirmDialogV2({
  open,
  title,
  message,
  confirmLabel = "Onayla",
  cancelLabel = "İptal",
  variant = "primary",
  confirmDisabled = false,
  errorText,
  className,
  onConfirm,
  onCancel,
}: ConfirmDialogV2Props) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const prevOverflowRef = useRef<string>("");
  const uid = useId();
  const titleId = `${uid}-title`;
  const messageId = `${uid}-message`;

  // Store previous focus & lock body overflow on open
  useEffect(() => {
    if (open) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      prevOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      setTimeout(() => confirmRef.current?.focus(), 50);
    }
    return () => {
      if (open) {
        document.body.style.overflow = prevOverflowRef.current;
        previousFocusRef.current?.focus();
      }
    };
  }, [open]);

  // Focus trap: Tab/Shift+Tab cycle inside dialog
  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab" || !dialogRef.current) return;

    const focusables =
      dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // ESC to cancel — stopPropagation prevents WizardShell ESC conflict
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    },
    [onCancel]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKey, true);
    document.addEventListener("keydown", handleFocusTrap);
    return () => {
      document.removeEventListener("keydown", handleKey, true);
      document.removeEventListener("keydown", handleFocusTrap);
    };
  }, [open, handleKey, handleFocusTrap]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      role="presentation"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity animate-in fade-in duration-150"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        className={cn(
          "relative z-10 mx-4 w-full max-w-sm rounded-[var(--v2-radius-md)] border border-current/10 bg-white text-slate-900 shadow-2xl animate-in zoom-in-95 fade-in duration-150",
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">
          <h3 id={titleId} className="mb-2 text-base font-bold text-inherit">
            {title}
          </h3>
          <p
            id={messageId}
            className="text-sm leading-relaxed text-inherit opacity-70"
          >
            {message}
          </p>
          {errorText && (
            <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {errorText}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 rounded-b-[var(--v2-radius-md)] border-t border-current/10 bg-black/5 px-5 py-3">
          <ButtonV2
            type="button"
            variant="ghost"
            className="min-h-8 px-4 py-1.5 text-sm"
            onClick={onCancel}
          >
            {cancelLabel}
          </ButtonV2>
          <ButtonV2
            ref={confirmRef}
            type="button"
            variant={variant === "danger" ? "danger" : "primary"}
            className="min-h-8 px-4 py-1.5 text-sm"
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </ButtonV2>
        </div>
      </div>
    </div>
  );
}
